import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type FeatureFlagsService from 'garaje/services/feature-flags';

import { PREVIOUS_VISITS_PAGE_SIZE, UPCOMING_VISITS_PAGE_SIZE, type VisitorsProfilesShowModel } from './route';

type TabValue = 'visits' | 'aliases' | 'documents';

export default class VisitorsProfilesShowController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;

  declare model: VisitorsProfilesShowModel;

  queryParams = ['upcomingVisitsCursor', 'previousVisitsCursor', 'from', 'id', 'tab'];
  @tracked upcomingVisitsCursor: string | null = null;
  @tracked previousVisitsCursor: string | null = null;
  @tracked from: string | null = null;
  @tracked id: string | null = null;
  @tracked tab: TabValue = 'visits';

  @tracked upcomingVisitsIsLoading: boolean = false;
  @tracked previousVisitsIsLoading: boolean = false;

  get upcomingVisitsPageSize(): number {
    return UPCOMING_VISITS_PAGE_SIZE;
  }

  get previousVisitsPageSize(): number {
    return PREVIOUS_VISITS_PAGE_SIZE;
  }

  get canAccessDocumentUploads(): boolean {
    return this.abilities.can('access document-upload');
  }

  get canAccessProfileNotes(): boolean {
    return this.featureFlags.isEnabled('vis-profile-notes');
  }

  get activeTab(): TabValue {
    if (this.tab === 'documents' && !this.canAccessDocumentUploads) {
      return 'visits';
    }
    return this.tab;
  }

  get backLink(): { route: string; model?: string } {
    // Use undefined instead of empty string so the array helper can conditionally build @models
    return { route: this.#routeFrom, model: this.id || undefined };
  }

  @action
  setUpcomingVisitsCursor(cursor: string | null): void {
    this.upcomingVisitsIsLoading = true;
    this.upcomingVisitsCursor = cursor;
  }

  @action
  setPreviousVisitsCursor(cursor: string | null): void {
    this.previousVisitsIsLoading = true;
    this.previousVisitsCursor = cursor;
  }

  @action
  setTab(tab: TabValue): void {
    this.tab = tab;
  }

  get #routeFrom(): string {
    switch (this.from) {
      case 'entry':
        return 'visitors.entry';
      case 'invite':
        return 'visitors.invites.show';
      default:
        return 'visitors.profiles';
    }
  }
}
